import { ActionType as AT } from './enums/action-type.enum';
import { Application } from './interfaces/application.interface';
import { SpecialOffer } from './interfaces/special-offer.interface';
import * as actions from './client.actions';
import { Doctor } from '../../models/doctor.model';
import { Service } from '../../models/service.model';
import { ABTestCase } from '../../models/ab-test.model';
import { Lead } from '../../models/lead.model';
import { ConvrTestVariation } from '../../interfaces/convr-test-variation.interface';

type InferValueTypes<T> = T extends { [key: string]: infer U } ? U : never;
type Action = ReturnType<InferValueTypes<typeof actions>>;

interface ClientState {
	clientId?: string;
	scenario?: 'fullPage' | 'popup' | 'custom';
	serviceList?: Service[];
	moreInfo?: string;
	privacyPolicy?: string;
	application?: Application;
	doctor?: Partial<Doctor>
	header?: { [key: string]: string | number | undefined | boolean };
	calculator?: Record<string, any>;
	hideCalculator?: boolean;
	hideDateForm?: boolean;
	hideAdvantages?: boolean;
	preFinalPage?: {
		banner?: string;
	}
	lead?: Lead;
	queryParams?: { [key: string]: any };
	customLeadParams?: { [key: string]: any };
	domain?: string;
	location?: string;
	accessKey?: string;
	license?: string;
	abCases?: ABTestCase[];
	initialViewContent?: {
		offer: string,
		description: string,
		dynamicText: string[] | null | undefined
	};
	customLabel?: {
		logo?: string
		text?: string
		link?: string
	};
	whiteLabel?: boolean;
	additionalCallbackButton?: {
		value: string
		link: string
	},
	whatsApp?: {
		enable: boolean
		phone: string,
		onFirstScreen?: boolean;
		inDoctorBlock?: boolean;
		start_page_text?: string;
		form_page_text?: string;
	}
	bonusHeaderInProcess?: string;
	bonusHeaderReady?: string;
	specialOffers?: {
		enable: boolean,
		list: SpecialOffer[]
	}
	bonusesStatusFromPlugin?: boolean,
	showCommonServiceList?: boolean,
	commonServiceList?: Service[],
	hideFlyingButton?: boolean,
	gotEntireData?: boolean,
	gotCustomContent?: boolean,
	testVariations?: ConvrTestVariation[],
	customContent?: {
		application?: Record<keyof Application, boolean>;
		initialView?: Record<keyof Application, boolean>;
		header?: Record<string, boolean>;
		doctor?: boolean;
		privacyPolicy?: boolean;
		footer?: boolean;
		moreInfo?: boolean;
		backgroundImage?: boolean;
		hideCalculator?: boolean;
		preFinalPage?: {
			header?: string;
			subHeader?: string;
		},
		finalPage?: {
			headerAfterTest?: string;
			headerAfterSignUp?: string;
			subHeader?: string;
		}
	},
	backgroundImage?: string;
	closeButton?: {
		show: boolean;
	},
	startPageAutoAction?: {
		action: "start_quiz" | "open_sign_up",
		timer: number //seconds
	}
	policyAgree?: boolean;
	policyAgreementMode?: "hard" | "light";
	policyAgreementNotificationText?: string;
}

export const clientReducer = (state: ClientState | null = null, action: Action): ClientState | null => {
	switch (action.type) {
		case AT.DEFINE_CLIENT_DATA: {
			var newState: ClientState = { ...state, ...action.payload.data };

			if (state?.header?.phoneIsReplaced) {
				newState = { ...newState, header: { ...newState.header, phone: state.header.phone } };
			}

			if (state?.whatsApp) {
				newState = {
					...newState, whatsApp: {
						start_page_text: state.whatsApp?.start_page_text || "Узнать цены!",
						form_page_text: state.whatsApp?.form_page_text || "Нажмите «Отправить» и получите расчет.",
						...state.whatsApp
					}
				};
			}

			if (!action.payload.data.specialOffers) {
				newState = { ...newState, specialOffers: undefined };
			}

			if (action.payload.data.customLeadParams) {
				newState = { ...newState, customLeadParams: { ...state?.customLeadParams, ...action.payload.data.customLeadParams } }
			}

			if (action.payload.data.application) {
				newState = { ...newState, application: { ...state?.application, ...action.payload.data.application } };
			}

			if (state?.customContent) {
				if (state.customContent.doctor) newState.doctor = state.doctor;
				if (state.customContent.footer) newState.license = state.license;
				if (state.customContent.header) newState.header = state.header;
				if (state.customContent.moreInfo) newState.moreInfo = state.moreInfo;
			}

			return newState;
		}

		case AT.UPDATE_DOCTOR: {
			return { ...state, doctor: { ...state?.doctor, ...action.payload.doctor } };
		}

		case AT.ADD_LEAD: {
			const { lead } = action.payload;
			return { ...state, lead: { ...state?.lead, ...lead } };
		}

		case AT.UPDATE_LEAD: {
			const { leadFields } = action.payload;
			return { ...state, lead: { ...state?.lead as Lead, ...leadFields } };
		}

		case AT.REPLACE_PHONE: {
			return { ...state, header: { ...state?.header, phone: action.payload.phone, phoneIsReplaced: true } };
		}

		case AT.DEFINE_AB_TEST_CASE: {
			const abCases = state?.abCases || [];
			return { ...state, abCases: [...abCases, action.payload.abTestCase] }
		}

		case AT.SET_PHONE_FORM_DATA: {
			return { ...state, application: { ...state?.application, ...action.payload } }
		}

		case AT.ENABLE_SPECIAL_OFFERS: {
			return { ...state, specialOffers: { ...state?.specialOffers as any, enable: action.payload.status } }
		}

		case AT.ENABLE_WHATS_APP: {
			return { ...state, whatsApp: { ...state?.whatsApp as any, enable: action.payload.status } }
		}

		case AT.UPDATE_SERVICE_LIST: {
			return { ...state, serviceList: action.payload.list }
		}

		case AT.ENABLE_COMMON_SERVICE_LIST: {
			return { ...state, showCommonServiceList: action.payload.status }
		}

		case AT.ADD_TEST_VARIATIONS: {
			return { ...state, testVariations: action.payload.variations };
		}

		case AT.UPDATE_CUSTOM_LEAD_PARAMS: {
			return { ...state, customLeadParams: { ...state?.customLeadParams, ...action.payload.params } }
		}

		case AT.ADD_CUSTOM_CONTENT: {
			if (state) {
				const newState = { ...state, gotCustomContent: true };
				const { doctor, privacyPolicy, footer, moreInfo, header, application, backgroundImage, hideFlyingButton, hideCalculator, preFinalPage, finalPage } = action.payload.customContent;

				newState.doctor = doctor ? doctor : newState.doctor;
				newState.hideFlyingButton = hideFlyingButton ?? newState.hideFlyingButton;
				newState.hideCalculator = hideCalculator ?? newState.hideCalculator;
				newState.privacyPolicy = privacyPolicy ? privacyPolicy : newState.privacyPolicy;
				newState.license = footer ? footer : newState.license;
				newState.moreInfo = moreInfo ? moreInfo : newState.moreInfo;
				if (header) newState.header = { ...newState.header, ...header };
				if (application) newState.application = { ...newState.application, ...application };
				newState.backgroundImage = backgroundImage ? backgroundImage : newState.backgroundImage;

				newState.customContent = {
					...newState.customContent,
					doctor: Boolean(doctor),
					privacyPolicy: Boolean(privacyPolicy),
					footer: Boolean(footer),
					moreInfo: Boolean(moreInfo),
					backgroundImage: Boolean(backgroundImage),
					preFinalPage: preFinalPage,
					finalPage: finalPage
				};


				if (application) {
					const applicationBooleanFields: Record<string, boolean> = {};
					for (let field in application) {
						applicationBooleanFields[field] = Boolean(application[field]);
					}

					newState.customContent.application = applicationBooleanFields;
				}

				if (header) {
					const headerBooleanFields: Record<string, boolean> = {};
					for (let field in header) {
						headerBooleanFields[field] = Boolean(header[field]);
					}

					newState.customContent.header = headerBooleanFields;
				}
				return newState;
			}

			return state;
		}

		case AT.UPDATE_CALCULATOR: {
			const { calculator } = action.payload;
			return { ...state, calculator: { ...state?.calculator, ...calculator } };
		}

		case AT.SET_POLICY_AGREE_STATUS: {
			return { ...state, policyAgree: true };
		}

		default: return state;
	}
}